class ExpressOrderCart extends HTMLElement {
  constructor() {
    super();
    this.selectedProducts = this.getStoredProducts();
    this.cartItemsContainer = this.querySelector('#cart-items');
    this.clearAllBtn = this.querySelector('#clear-all-btn');
    this.cartTotal = this.querySelector('#cart-total');
    this.checkoutBtn = document.querySelector('#checkout-btn');
    this.quoteBtn = document.querySelector('#express-quote-btn');

    this.init();
  }

  init() {
    this.cartQuantities = {};
    this.bindEvents();
    this.refreshCartQuantities().then(() => this.updateDisplay());
  }

  refreshCartQuantities() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        const map = {};
        if (cart && Array.isArray(cart.items)) {
          for (const item of cart.items) {
            const id = String(item.variant_id);
            map[id] = (map[id] || 0) + (parseInt(item.quantity, 10) || 0);
          }
        }
        this.cartQuantities = map;
      })
      .catch(() => {
        this.cartQuantities = {};
      });
  }

  getCartQty(variantId) {
    return this.cartQuantities[String(variantId)] || 0;
  }

  getEffectiveUnitPriceForItem(product) {
    const cartQty = this.getCartQty(product.variantId);
    const merged = { ...product, quantity: (parseInt(product.quantity, 10) || 0) + cartQty };
    return ExpressOrderCart.getEffectiveUnitPrice(merged);
  }

  bindEvents() {
    document.addEventListener('express-order-add-product', (e) => {
      this.addProductToCart(e.detail);
    });

    document.addEventListener('quantity-change', (e) => {
      this.updateQuantity(e.detail.variantId, e.detail.quantity);
    });

    document.addEventListener('remove-item', (e) => {
      this.removeProduct(e.detail.variantId);
    });

    document.addEventListener('remove-all', () => {
      this.clearAllProducts();
    });

    if (this.checkoutBtn) {
      this.checkoutBtn.addEventListener('click', () => this.handleCheckout());
    }

    if (this.clearAllBtn) {
      this.clearAllBtn.addEventListener('click', () => this.clearAllProducts());
    }
  }

  addProductToCart(productData) {
    const cleanProductData = {
      variantId: productData.variantId,
      handle: productData.handle || '',
      title: productData.title || productData.variantTitle || 'Produit sans nom',
      variantTitle: productData.variantTitle || '',
      price: parseInt(productData.price) || 0,
      compareAtPrice: parseInt(productData.compareAtPrice) || 0,
      quantity: parseInt(productData.quantity) || 1,
      image:
        productData.image &&
        productData.image !== 'Liquid error (sections/express-order-search line 67): invalid url input'
          ? productData.image
          : '',
      sku: productData.sku || '',
    };

    cleanProductData.salesMultiple = parseInt(productData.salesMultiple) || 0;
    cleanProductData.priceBreaks = ExpressOrderCart.normalizePriceBreaks(productData.priceBreaks);

    const existingIndex = this.selectedProducts.findIndex((p) => p.variantId === cleanProductData.variantId);

    if (existingIndex >= 0) {
      this.selectedProducts[existingIndex].quantity += cleanProductData.quantity;
    } else {
      this.selectedProducts.push(cleanProductData);
    }

    this.saveProducts();
    this.updateDisplay();
  }

  removeProduct(variantId) {
    this.selectedProducts = this.selectedProducts.filter((p) => String(p.variantId) !== String(variantId));
    this.saveProducts();
    this.updateDisplay();
  }

  updateQuantity(variantId, newQuantity) {
    const product = this.selectedProducts.find((p) => String(p.variantId) === String(variantId));

    if (product && newQuantity > 0) {
      product.quantity = newQuantity;
      this.saveProducts();
      this.updateDisplay();
    }
  }

  clearAllProducts() {
    this.selectedProducts = [];
    this.saveProducts();
    this.updateDisplay();
  }

  updateDisplay() {
    if (this.selectedProducts.length === 0) {
      this.showEmptyState();
      return;
    }
    this.renderCart();
  }

  renderCart() {
    if (!this.cartItemsContainer) return;

    const itemsHtml = this.selectedProducts.map((p) => this.renderItem(p)).join('');
    this.cartItemsContainer.innerHTML = itemsHtml;

    const totalPrice = this.selectedProducts.reduce(
      (sum, p) => sum + this.getEffectiveUnitPriceForItem(p) * p.quantity,
      0,
    );
    const subtotal = this.selectedProducts.reduce((sum, p) => sum + (p.compareAtPrice || p.price) * p.quantity, 0);
    const totalItems = this.selectedProducts.reduce((sum, p) => sum + p.quantity, 0);
    const discount = subtotal - totalPrice;

    if (this.cartTotal) {
      this.cartTotal.style.display = 'flex';

      const subtotalElement = this.cartTotal.querySelector('#subtotal-amount');
      if (subtotalElement) subtotalElement.textContent = this.formatPrice(subtotal);

      const discountElement = this.cartTotal.querySelector('#discount-amount');
      if (discountElement) {
        discountElement.textContent = discount > 0 ? '-' + this.formatPrice(discount) : '-' + this.formatPrice(0);
        const discountRow = discountElement.closest('.express-order__total-line--discount');
        if (discountRow) discountRow.style.display = discount > 0 ? 'flex' : 'none';
      }

      const totalAmountElement = this.cartTotal.querySelector('#total-amount');
      if (totalAmountElement) totalAmountElement.textContent = this.formatPrice(totalPrice) + ' HT';
    }

    if (this.clearAllBtn) this.clearAllBtn.style.display = 'flex';

    this.updateSummary({ totalItems, totalPrice, totalPriceFormatted: this.formatPrice(totalPrice) });
  }

  renderItem(p) {
    const sm = parseInt(p.salesMultiple) || 0;
    const min = sm > 0 ? sm : 1;
    const step = sm > 0 ? sm : 1;
    const qty = sm > 0 && p.quantity < sm ? sm : p.quantity;
    const effectivePrice = this.getEffectiveUnitPriceForItem(p);
    const hasVolumeDiscount = effectivePrice < p.price;

    const imageHtml = p.image
      ? `<img src="${this.escapeAttr(p.image)}" alt="${this.escapeAttr(p.title)}" loading="lazy">`
      : `<svg xmlns="http://www.w3.org/2000/svg" class="placeholder-svg placeholder" viewBox="0 0 525.5 525.5"><path d="M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V315c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-99.5c0-1.6-1.3-2.8-2.9-2.8zm1 102.3c0 .5-.4 1-1 1H203c-.5 0-1-.4-1-1v-99.5c0-.5.4-1 1-1h121.6c.5 0 1 .4 1 1V315z"/><path d="M210.4 281.7c.2.2.4.3.7.3.2 0 .5-.1.6-.2l27.4-22.7 36.7 38.6c.2.2.4.3.7.3.2 0 .4-.1.6-.2.4-.3.4-.9.1-1.3l-22.4-23.6 23.4-29.4 38.6 35.8c.4.3 1 .3 1.3-.1.3-.4.3-1-.1-1.3l-39.3-36.5c-.2-.2-.4-.2-.7-.2s-.5.1-.6.3l-23.8 30-13.2-13.9c-.3-.4-.9-.4-1.3-.1l-28 23.2c-.4.4-.4 1-.7 1z"/><circle cx="223.6" cy="232.8" r="4.5"/></svg>`;

    const variantHtml = p.variantTitle && p.variantTitle !== 'Default Title'
      ? `<p class="express-order__item-variant text-body">${this.escapeHtml(p.variantTitle)}</p>`
      : '';

    const skuHtml = p.sku
      ? `<p class="express-order__item-sku text-sm">SKU: ${this.escapeHtml(p.sku)}</p>`
      : '';

    const compareHtml = p.compareAtPrice && p.compareAtPrice > p.price
      ? `<span class="express-order__compare-price text-sm">${this.formatPrice(p.compareAtPrice)}</span>`
      : hasVolumeDiscount
        ? `<span class="express-order__compare-price text-sm">${this.formatPrice(p.price)}</span>`
        : '';

    const smAttr = sm > 0 ? ` data-sales-multiple="${sm}"` : '';

    return `
      <express-order-item data-variant-id="${this.escapeAttr(p.variantId)}" data-handle="${this.escapeAttr(p.handle)}"${smAttr}>
        <div class="express-order__cart-item">
          <div class="express-order__item-image">${imageHtml}</div>
          <div class="express-order__item-content">
            <div class="express-order__item-details">
              <div class="express-order__item-details-infos">
                <h4 class="express-order__item-title text-body">${this.escapeHtml(p.title)}</h4>
                ${variantHtml}
                ${skuHtml}
              </div>
              <div class="express-order__item-price">
                <span class="express-order__price h6">${this.formatPrice(effectivePrice)}</span>
                ${compareHtml}
              </div>
            </div>
            <div class="express-order__item-controls">
              <div class="express-order__quantity-controls">
                <button class="express-order__qty-btn" data-action="decrease" type="button">${ExpressOrderCart.ICON_MINUS}</button>
                <input type="number" class="express-order__qty-input" value="${qty}" min="${min}" step="${step}">
                <button class="express-order__qty-btn" data-action="increase" type="button">${ExpressOrderCart.ICON_PLUS}</button>
              </div>
              <button class="express-order__remove-btn" data-action="remove" type="button">
                Supprimer
                <span>${ExpressOrderCart.ICON_CROSS}</span>
              </button>
            </div>
          </div>
        </div>
      </express-order-item>
    `;
  }

  escapeHtml(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  }

  escapeAttr(s) {
    return this.escapeHtml(s);
  }

  showEmptyState() {
    if (this.cartItemsContainer) {
      this.cartItemsContainer.innerHTML = `
        <div class="express-order__empty-state">
          <p>Aucun produit sélectionné</p>
          <p class="express-order__empty-subtitle">Utilisez la recherche ci-dessus pour ajouter des produits</p>
        </div>
      `;
    }

    if (this.clearAllBtn) {
      this.clearAllBtn.style.display = 'none';
    }

    if (this.cartTotal) {
      this.cartTotal.style.display = 'none';
    }

    this.updateSummary({ totalItems: 0, totalPrice: 0 });
  }

  updateSummary(cartData = null) {
    let itemsCount, totalPrice;

    if (cartData) {
      itemsCount = cartData.totalItems || 0;
      totalPrice = cartData.totalPrice || 0;
    } else {
      itemsCount = this.selectedProducts.reduce((sum, product) => sum + product.quantity, 0);
      totalPrice = this.selectedProducts.reduce(
        (sum, product) => sum + this.getEffectiveUnitPriceForItem(product) * product.quantity,
        0,
      );
    }

    const summaryItemsCount = document.querySelector('#summary-items-count');
    const summaryTotalAmount = document.querySelector('#summary-total-amount');
    const summaryOriginalPrice = document.querySelector('.express-order__summary-pricing-original');
    const summaryBadge = document.querySelector('.express-order__summary-pricing-badge');
    const summaryCartImages = document.querySelector('#summary-cart-images');

    if (summaryItemsCount) {
      summaryItemsCount.textContent = itemsCount;
    }

    if (summaryCartImages) {
      summaryCartImages.innerHTML = '';
      this.selectedProducts.slice(0, 3).forEach((product) => {
        if (product.image) {
          const img = document.createElement('img');
          img.src = product.image;
          img.alt = product.title;
          img.width = 40;
          img.height = 40;
          img.loading = 'lazy';
          img.onerror = () => {
            img.replaceWith(this.createPlaceholderSvg());
          };
          summaryCartImages.appendChild(img);
        } else {
          summaryCartImages.appendChild(this.createPlaceholderSvg());
        }
      });

      if (this.selectedProducts.length > 3) {
        const moreCount = document.createElement('div');
        moreCount.className = 'express-order__summary-cart-more';
        moreCount.textContent = `+${this.selectedProducts.length - 3}`;
        summaryCartImages.appendChild(moreCount);
      }
    }

    if (summaryTotalAmount) {
      if (cartData && cartData.totalPriceFormatted) {
        summaryTotalAmount.textContent = cartData.totalPriceFormatted;
      } else {
        summaryTotalAmount.textContent = this.formatPrice(totalPrice);
      }
    }

    const subtotal = this.selectedProducts.reduce((sum, product) => {
      const price = product.compareAtPrice && product.compareAtPrice > product.price ? product.compareAtPrice : product.price;
      return sum + price * product.quantity;
    }, 0);

    // Update original price and badge visibility
    if (summaryOriginalPrice) {
      if (subtotal > totalPrice) {
        summaryOriginalPrice.textContent = this.formatPrice(subtotal);
        summaryOriginalPrice.style.display = 'block';
      } else {
        summaryOriginalPrice.style.display = 'none';
      }
    }

    if (summaryBadge) {
      if (subtotal > totalPrice) {
        const discountPercentage = Math.round(((subtotal - totalPrice) / subtotal) * 100);
        const badgeSpan = summaryBadge.querySelector('span');
        if (badgeSpan) {
          badgeSpan.textContent = `-${discountPercentage}%`;
        }
        summaryBadge.style.display = 'flex';
      } else {
        summaryBadge.style.display = 'none';
      }
    }

    if (this.checkoutBtn) {
      this.checkoutBtn.disabled = itemsCount === 0;
    }
    if (this.quoteBtn) {
      this.quoteBtn.disabled = itemsCount === 0;
    }
  }

  async handleCheckout() {
    if (this.selectedProducts.length === 0) return;

    const expressOrderId = this.generateExpressOrderId();
    const cartItems = this.selectedProducts.map((product) => ({
      id: parseInt(product.variantId),
      quantity: product.quantity,
      properties: {
        _express_order: expressOrderId,
      },
    }));

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items: cartItems }),
      });

      if (response.ok) {
        this.clearAllProducts();
        window.location.href = '/cart';
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || errorData.message || "Erreur lors de l'ajout au panier");
      }
    } catch (error) {
      alert("Une erreur est survenue lors de l'ajout au panier: " + error.message);
    }
  }

  generateExpressOrderId() {
    const timestamp = Date.now();
    return btoa(timestamp.toString())
      .replace(/[^a-zA-Z0-9]/g, '')
      .substring(0, 8);
  }

  getStoredProducts() {
    try {
      const stored = localStorage.getItem('selectedProducts');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      return [];
    }
  }

  saveProducts() {
    try {
      localStorage.setItem('selectedProducts', JSON.stringify(this.selectedProducts));
    } catch (error) {
      console.error('[ExpressOrderCart] Erreur sauvegarde localStorage:', error);
    }
  }

  createPlaceholderSvg() {
    const wrapper = document.createElement('div');
    wrapper.className = 'express-order__summary-placeholder';
    wrapper.style.cssText = 'width:7.2rem;height:8.8rem;margin-right:-6.4rem;display:flex;align-items:center;justify-content:center;background:#f0f0f0;box-shadow:-2px 1px 6px 0px rgba(0,0,0,0.1);';
    wrapper.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 20 20" fill="none"><path d="M3.333 15.833V4.167h13.334v11.666H3.333Z" stroke="#999" stroke-width="1.2"/><path d="M3.333 12.5l3.75-3.75 2.5 2.5 3.334-3.333 3.75 3.75" stroke="#999" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="7.5" cy="7.5" r="1.25" stroke="#999" stroke-width="1.2"/></svg>';
    return wrapper;
  }

  formatPrice(price) {
    if (!price) return '0,00 €';
    const euros = (price / 100).toFixed(2).replace('.', ',');
    return `${euros} €`;
  }

  getProducts() {
    return [...this.selectedProducts];
  }

  getTotalPrice() {
    return this.selectedProducts.reduce(
      (sum, product) => sum + this.getEffectiveUnitPriceForItem(product) * product.quantity,
      0,
    );
  }

  getTotalItems() {
    return this.selectedProducts.reduce((sum, product) => sum + product.quantity, 0);
  }
}

ExpressOrderCart.normalizePriceBreaks = function (raw) {
  if (!raw) return [];
  let arr = raw;
  if (typeof raw === 'string') {
    try {
      arr = JSON.parse(raw);
    } catch (e) {
      return [];
    }
  }
  if (!Array.isArray(arr)) return [];
  return arr
    .map((b) => ({
      minimum_quantity: parseInt(b.minimum_quantity, 10),
      price: parseInt(b.price, 10),
    }))
    .filter((b) => Number.isFinite(b.minimum_quantity) && Number.isFinite(b.price))
    .sort((a, b) => b.minimum_quantity - a.minimum_quantity);
};

ExpressOrderCart.getEffectiveUnitPrice = function (product) {
  const basePrice = parseInt(product.price, 10) || 0;
  const breaks = ExpressOrderCart.normalizePriceBreaks(product.priceBreaks);
  if (breaks.length === 0) return basePrice;
  const qty = parseInt(product.quantity, 10) || 0;
  for (const pb of breaks) {
    if (qty >= pb.minimum_quantity) return pb.price;
  }
  return basePrice;
};

ExpressOrderCart.ICON_MINUS = '<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-minus" width="20" height="21" viewBox="0 0 20 21" fill="none"><path d="M16.6666 10.5024H3.33325" stroke="#443D36" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
ExpressOrderCart.ICON_PLUS = '<svg class="icon icon-plus" xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none"><path d="M9.99992 3.83582V17.1691M3.33325 10.5025H16.6666" stroke="#443D36" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
ExpressOrderCart.ICON_CROSS = '<svg class="icon icon-cross" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M15 1L1 15M1 1L15 15" stroke="#8E8276" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

class ExpressOrderItem extends HTMLElement {
  connectedCallback() {
    this.bindEvents();
  }

  bindEvents() {
    const qtyInput = this.querySelector('.express-order__qty-input');
    const decreaseBtn = this.querySelector('[data-action="decrease"]');
    const increaseBtn = this.querySelector('[data-action="increase"]');
    const removeBtn = this.querySelector('[data-action="remove"]');

    if (qtyInput) {
      qtyInput.addEventListener('change', (e) => this.handleQuantityChange(e));
    }

    if (decreaseBtn) {
      decreaseBtn.addEventListener('click', () => this.handleDecrease());
    }

    if (increaseBtn) {
      increaseBtn.addEventListener('click', () => this.handleIncrease());
    }

    if (removeBtn) {
      removeBtn.addEventListener('click', () => this.handleRemove());
    }
  }

  handleQuantityChange(e) {
    let newQuantity = parseInt(e.target.value) || 1;
    const salesMultiple = parseInt(this.dataset.salesMultiple) || 0;
    const variantId = this.dataset.variantId;

    if (salesMultiple > 0) {
      newQuantity = Math.round(newQuantity / salesMultiple) * salesMultiple;
      if (newQuantity < salesMultiple) newQuantity = salesMultiple;
      e.target.value = newQuantity;
    }

    this.dispatchEvent(
      new CustomEvent('quantity-change', {
        detail: { variantId, quantity: newQuantity },
        bubbles: true,
      }),
    );
  }

  handleDecrease() {
    const qtyInput = this.querySelector('.express-order__qty-input');
    const currentQty = parseInt(qtyInput.value) || 1;
    const salesMultiple = parseInt(this.dataset.salesMultiple) || 0;
    const stepValue = salesMultiple > 0 ? salesMultiple : 1;
    const minValue = salesMultiple > 0 ? salesMultiple : 1;

    if (currentQty > minValue) {
      qtyInput.value = currentQty - stepValue;
      if (parseInt(qtyInput.value) < minValue) qtyInput.value = minValue;
      this.handleQuantityChange({ target: qtyInput });
    }
  }

  handleIncrease() {
    const qtyInput = this.querySelector('.express-order__qty-input');
    const currentQty = parseInt(qtyInput.value) || 1;
    const salesMultiple = parseInt(this.dataset.salesMultiple) || 0;
    const stepValue = salesMultiple > 0 ? salesMultiple : 1;

    qtyInput.value = currentQty + stepValue;
    this.handleQuantityChange({ target: qtyInput });
  }

  handleRemove() {
    const variantId = this.dataset.variantId;

    this.dispatchEvent(
      new CustomEvent('remove-item', {
        detail: { variantId },
        bubbles: true,
      }),
    );
  }
}

class ExpressOrderSummary extends HTMLElement {
  constructor() {
    super();
    this.cartImagesContainer = this.querySelector('#summary-cart-images');
    this.removeAllBtn = this.querySelector('#summary-remove-all-btn');
  }

  connectedCallback() {
    this.bindEvents();
  }

  bindEvents() {
    // Lier le bouton de suppression à l'événement remove-all
    if (this.removeAllBtn) {
      this.removeAllBtn.addEventListener('click', () => {
        document.dispatchEvent(new CustomEvent('remove-all', { bubbles: true }));
      });
    }
  }
}

// Définir les custom elements
if (!customElements.get('express-order-cart')) {
  customElements.define('express-order-cart', ExpressOrderCart);
}

if (!customElements.get('express-order-item')) {
  customElements.define('express-order-item', ExpressOrderItem);
}

if (!customElements.get('express-order-summary')) {
  customElements.define('express-order-summary', ExpressOrderSummary);
}
